import { useState, useEffect, useCallback } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Shield,
  FileBarChart,
  Clock,
  FileArchive,
  Share2,
  Server,
  BookOpen,
  AlertTriangle,
  Trash2,
  X,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { PageShell } from "../components/layout/PageShell";
import { Modal, ModalShell } from "../components/Modal";
import { Tip } from "../components/ui/tooltip";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { REPORTS_URL } from "../../constants";

/* ── Types ──────────────────────────────────────────────── */

interface AssessmentPlan {
  planId: string;
  title: string;
  assessmentStatus: string;
  lifecycleStatus: string;
  controlCount: number;
  passed: number;
  failed: number;
  compliancePercent: number;
  totalStatements: number;
}

interface ReportActions {
  summary: string;
  preview: string;
  export: string;
  exportWithEvidence: string;
}

interface ReportSummary {
  sspId: string;
  systemName: string;
  lifecycleStatus: string;
  createdAt: string;
  updatedAt: string;
  planCount: number;
  hasAssessment: boolean;
  compliancePercent: number;
  overallStatus: string;
  assessmentPlans: AssessmentPlan[];
  actions: ReportActions;
}

/* ── Helpers ────────────────────────────────────────────── */

const toFullUrl = (path: string) => {
  if (path.startsWith("http")) return path;
  const normalized =
    path.startsWith("/api/") && !path.startsWith("/api/v1/")
      ? path.replace("/api/", "/api/v1/")
      : path;
  return `${window.BE_URL || ""}${normalized}`;
};

const buildReportFilename = (
  systemName: string,
  withEvidence: boolean,
): string => {
  const safe =
    (systemName || "").replace(/[\\/:*?"<>|]+/g, "").trim() || "report";
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  const suffix = withEvidence ? " (with evidence)" : "";
  return `${safe} ${stamp}${suffix}.zip`;
};

const statusColor = (s: string) => {
  const key = (s || "").toUpperCase();
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    COMPLETED: {
      bg: "bg-emerald-50 dark:bg-emerald-500/15",
      text: "text-emerald-700 dark:text-emerald-400",
      dot: "bg-emerald-500",
    },
    IN_PROGRESS: {
      bg: "bg-amber-50 dark:bg-amber-500/15",
      text: "text-amber-700 dark:text-amber-400",
      dot: "bg-amber-500",
    },
    NOT_STARTED: {
      bg: "bg-slate-100 dark:bg-slate-500/15",
      text: "text-slate-600 dark:text-slate-400",
      dot: "bg-slate-400",
    },
    NO_ASSESSMENT: {
      bg: "bg-red-50 dark:bg-red-500/15",
      text: "text-red-700 dark:text-red-400",
      dot: "bg-red-500",
    },
  };
  return map[key] || map.NO_ASSESSMENT;
};

const complianceColor = (pct: number) =>
  pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";

const complianceTextColor = (pct: number) =>
  pct >= 80
    ? "text-emerald-600 dark:text-emerald-400"
    : pct >= 50
      ? "text-amber-600 dark:text-amber-400"
      : "text-red-600 dark:text-red-400";

/* ── Main component ─────────────────────────────────────── */

export function ReportDetails() {
  const { sspId } = useParams<{ sspId: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<ReportSummary | null>(null);
  // Publish the system this report covers, so reports.yaml's {item} questions apply. Named by the
  // system rather than the report, because that is what the user asks about. Cleared on unmount.
  useEffect(() => {
    const name = report?.systemName;
    useUiContextStore
      .getState()
      .setSelectedItem(sspId && name ? { id: sspId, name } : null);
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [sspId, report]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [headerDownloadOpen, setHeaderDownloadOpen] = useState(false);
  const [cardDownloadOpen, setCardDownloadOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [planExporting, setPlanExporting] = useState<string | null>(null);
  const [planDeleting, setPlanDeleting] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!sspId) return;
    setLoading(true);
    try {
      const res = await getAPI(REPORTS_URL.summary(sspId));
      setReport(Array.isArray(res) ? res[0] : (res?.data ?? res));
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to load report summary");
    } finally {
      setLoading(false);
    }
  }, [sspId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleDownload = async (withEvidence: boolean) => {
    if (!report || !sspId) return;
    const key = withEvidence ? "evidence" : "report";
    setHeaderDownloadOpen(false);
    setCardDownloadOpen(false);
    setDownloading(key);
    try {
      const url = withEvidence
        ? report.actions.exportWithEvidence
        : report.actions.export;
      const res = await downloadFile(toFullUrl(url));
      if (!res) return;
      const blob = new Blob([res.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = buildReportFilename(report.systemName, withEvidence);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      useToastStore.getState().showSuccess("Success", "Download started");
    } catch {
      useToastStore.getState().showError("Error", "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      useToastStore
        .getState()
        .showSuccess("Success", "Report link copied to clipboard");
    } catch {
      useToastStore.getState().showError("Error", "Failed to copy link");
    }
  };

  const handlePreview = async () => {
    if (!report?.actions.preview) return;
    setPreviewLoading(true);
    try {
      const url = toFullUrl(report.actions.preview);
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch preview");
      const html = await response.text();

      setPreviewHtml(html);
      setPreviewLoading(false);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to load preview");
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewHtml(null);
  };

  const handleExportPlan = async (plan: AssessmentPlan) => {
    setPlanExporting(plan.planId);
    try {
      // TODO: Implement actual export functionality
      setTimeout(() => {
        useToastStore
          .getState()
          .showSuccess("Success", `${plan.title} exported`);
        setPlanExporting(null);
      }, 2000);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to export plan");
      setPlanExporting(null);
    }
  };

  const handleDeletePlan = async (plan: AssessmentPlan) => {
    setPlanDeleting(plan.planId);
    try {
      // TODO: Implement actual delete functionality
      setTimeout(() => {
        useToastStore
          .getState()
          .showSuccess("Success", `${plan.title} deleted`);
        setPlanDeleting(null);
      }, 2000);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to delete plan");
      setPlanDeleting(null);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <PageShell className="flex items-center justify-center py-32">
        <Loader2 className="size-6 animate-spin text-indigo-500" />
        <span className="ml-2 text-sm d">Loading report</span>
      </PageShell>
    );
  }

  /* ── Not found ── */
  if (!report) {
    return (
      <PageShell className="space-y-6">
        <button
          onClick={() => navigate("/report")}
          className="flex items-center gap-1 text-sm d hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" /> Back to Reports
        </button>
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <FileBarChart className="size-8 d mx-auto mb-3" />
          <p className="text-sm font-medium">Report not found</p>
        </div>
      </PageShell>
    );
  }

  const totalPassed = report.assessmentPlans.reduce((s, p) => s + p.passed, 0);
  const totalFailed = report.assessmentPlans.reduce((s, p) => s + p.failed, 0);
  const totalControls = report.assessmentPlans.reduce(
    (s, p) => s + p.controlCount,
    0,
  );
  const remaining = totalControls - totalPassed - totalFailed;
  const sc = statusColor(report.overallStatus);

  return (
    <PageShell className="space-y-6">
      {/* ── Breadcrumb + Actions ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm d">
          <button
            onClick={() => navigate("/report")}
            className="hover:text-foreground transition-colors"
          >
            Reports
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">
            SSP Assessment Details
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setHeaderDownloadOpen((open) => !open)}
              disabled={!!downloading}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              title="Download report"
            >
              {downloading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              Export Data
            </button>
          </div>
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <Share2 className="size-3.5" />
            Share Report
          </button>
        </div>
      </div>

      {/* ── Report Title Card ── */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 dark:from-indigo-700 dark:to-indigo-600 px-6 py-5">
          <h1 className="text-lg font-bold text-white">
            Assessment Report: {report.systemName}
          </h1>
          <p className="text-indigo-100 text-xs mt-1">
            Full System Security Plan analysis for {report.systemName}
          </p>
        </div>

        {/* ── Executive Overview ── */}
        <div className="px-6 py-5 space-y-5">
          <h2 className="text-xs font-bold d tracking-wider">
            Executive Overview
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: SSP Info */}
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold d tracking-wider">
                  SSP Name
                </p>
                <p className="text-sm font-bold text-foreground mt-0.5">
                  {report.systemName}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold d tracking-wider">
                    Assessment Date
                  </p>
                  <p className="text-sm font-medium text-foreground mt-0.5">
                    {new Date(report.updatedAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold d tracking-wider">
                    Status
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className={`size-2 rounded-full ${sc.dot}`} />
                    <p className={`text-sm font-bold ${sc.text}`}>
                      {(report.overallStatus || "NO_ASSESSMENT").replace(
                        /_/g,
                        " ",
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold d tracking-wider">
                  Lifecycle Status
                </p>
                <span className="inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-[10px] font-bold ring-1 tracking-wide bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/20 dark:text-slate-400 dark:ring-slate-500/30">
                  {(report.lifecycleStatus || "draft").replace(/_/g, " ")}
                </span>
              </div>
            </div>

            {/* Right: Compliance Score */}
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/30 p-6">
              <p className="text-[10px] font-bold d tracking-wider mb-3">
                Overall Compliance Score
              </p>
              <div className="relative size-32">
                <svg viewBox="0 0 36 36" className="size-full -rotate-90">
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className="text-slate-200 dark:text-white/10"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    strokeWidth="3"
                    strokeDasharray={`${report.compliancePercent} ${100 - report.compliancePercent}`}
                    strokeLinecap="round"
                    className={complianceTextColor(report.compliancePercent)}
                    stroke="currentColor"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className={`text-2xl font-black ${complianceTextColor(report.compliancePercent)}`}
                  >
                    {report.compliancePercent}%
                  </span>
                  <span className="text-[9px] d font-medium">Compliant</span>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 text-xs">
                <span className="flex items-center gap-1">
                  <CheckCircle className="size-3 text-emerald-500" />
                  <span className="font-bold text-foreground">
                    {totalPassed}
                  </span>
                  <span className="d">Passed</span>
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="size-3 text-red-500" />
                  <span className="font-bold text-foreground">
                    {totalFailed}
                  </span>
                  <span className="d">Failed</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── System Components Score ── */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold d tracking-wider">
            System Components Score
          </h2>
          <span className="text-xs d">{totalControls} Controls assessed</span>
        </div>
        {/* Stacked bar */}
        <div className="flex h-6 rounded-lg overflow-hidden bg-slate-100 dark:bg-white/[0.06]">
          {totalPassed > 0 && (
            <div
              className="bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold transition-all"
              style={{
                width: `${(totalPassed / Math.max(totalControls, 1)) * 100}%`,
              }}
            >
              {totalPassed > 0 && totalPassed}
            </div>
          )}
          {totalFailed > 0 && (
            <div
              className="bg-red-500 flex items-center justify-center text-white text-[10px] font-bold transition-all"
              style={{
                width: `${(totalFailed / Math.max(totalControls, 1)) * 100}%`,
              }}
            >
              {totalFailed > 0 && totalFailed}
            </div>
          )}
          {remaining > 0 && (
            <div
              className="bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-white text-[10px] font-bold transition-all"
              style={{
                width: `${(remaining / Math.max(totalControls, 1)) * 100}%`,
              }}
            >
              {remaining > 0 && remaining}
            </div>
          )}
        </div>
        <div className="flex items-center gap-5 mt-3">
          <span className="flex items-center gap-1.5 text-xs">
            <span className="size-2.5 rounded-full bg-emerald-500" />
            <span className="d">Passed</span>
            <span className="font-bold text-foreground">{totalPassed}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="size-2.5 rounded-full bg-red-500" />
            <span className="d">Failed</span>
            <span className="font-bold text-foreground">{totalFailed}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="size-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
            <span className="d">Remaining</span>
            <span className="font-bold text-foreground">{remaining}</span>
          </span>
        </div>
      </div>

      {/* ── Control Implementation Results ── */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Shield className="size-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-foreground">
              Control Implementation Results
            </h2>
          </div>
          <span className="text-xs d">
            {report.assessmentPlans.length} Assessment Plan
            {report.assessmentPlans.length !== 1 ? "s" : ""}
          </span>
        </div>

        {report.assessmentPlans.length === 0 ? (
          <div className="p-10 text-center">
            <Shield className="size-8 d mx-auto mb-2" />
            <p className="text-sm d">No assessment plans linked to this SSP.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[11px] d tracking-wider">
                  <th className="px-5 py-3 text-center font-medium">#</th>
                  <th className="px-5 py-3 text-center font-medium">Plan Name</th>
                  <th className="px-5 py-3 text-center font-medium">Controls</th>
                  <th className="px-5 py-3 text-center font-medium">Passed</th>
                  <th className="px-5 py-3 text-center font-medium">Failed</th>
                  <th className="px-5 py-3 text-center font-medium">
                    Compliance
                  </th>
                  <th className="px-5 py-3 text-center font-medium">Status</th>
                  <th className="px-5 py-3 text-center font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {report.assessmentPlans.map((plan, idx) => {
                  const psc = statusColor(plan.assessmentStatus);
                  return (
                    <tr
                      key={plan.planId}
                      className="border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-5 py-3.5 text-left d tabular-nums">
                        {idx + 1}
                      </td>
                      <td className="px-5 py-3.5 font-medium text-foreground">
                        {plan.title}
                      </td>
                      <td className="px-5 py-3.5 text-right d tabular-nums">
                        {plan.controlCount}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold tabular-nums">
                          <CheckCircle className="size-3" />
                          {plan.passed}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-bold tabular-nums">
                          <XCircle className="size-3" />
                          {plan.failed}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-20 h-1.5 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
                            <div
                              className={`h-full rounded-full ${complianceColor(plan.compliancePercent)}`}
                              style={{
                                width: `${plan.compliancePercent}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-bold text-foreground w-8 text-right">
                            {plan.compliancePercent}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${psc.bg} ${psc.text}`}
                        >
                          <span
                            className={`size-1.5 rounded-full ${psc.dot}`}
                          />
                          {(plan.assessmentStatus || "NOT_STARTED").replace(
                            /_/g,
                            " ",
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportPlan(plan);
                            }}
                            disabled={planExporting === plan.planId}
                            className="p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-500/10 d hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-40"
                            title="Export plan"
                          >
                            {planExporting === plan.planId ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Download className="size-4" />
                            )}
                          </button>
                          <Tip
                            label={
                              plan.assessmentStatus === "COMPLETED"
                                ? "Cannot delete completed plans"
                                : "Delete Plan"
                            }
                            side="bottom"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlan(plan);
                              }}
                              disabled={
                                planDeleting === plan.planId ||
                                plan.assessmentStatus === "COMPLETED"
                              }
                              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 d hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:d"
                            >
                              {planDeleting === plan.planId ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Trash2 className="size-4 hover:text-red-600" />
                              )}
                            </button>
                          </Tip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Bottom Cards: POA&M + Download ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* POA&M Summary */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="size-4 text-amber-500" />
            <h2 className="text-sm font-bold text-foreground">
              Plan of Action & Milestones (POA&M)
            </h2>
          </div>
          <p className="text-xs d">
            Open remediation tasks for compliance gaps
          </p>
          {totalFailed > 0 ? (
            <div className="mt-4 space-y-2">
              {report.assessmentPlans
                .filter((p) => p.failed > 0)
                .map((p) => (
                  <div
                    key={p.planId}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20"
                  >
                    <span className="text-xs font-medium text-red-700 dark:text-red-400">
                      {p.title}
                    </span>
                    <span className="text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20 px-2 py-0.5 rounded-full">
                      {p.failed} failed
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-4 px-3 py-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
              <CheckCircle className="size-4 text-emerald-500" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                No open remediation items
              </span>
            </div>
          )}
        </div>

        {/* Export Options */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileBarChart className="size-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-foreground">
              Export Options
            </h2>
          </div>
          <div className="space-y-2">
            <div className="relative">
              <button
                onClick={() => setCardDownloadOpen((open) => !open)}
                disabled={!!downloading}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="size-5 animate-spin d" />
                ) : (
                  <Download className="size-5 text-indigo-500" />
                )}
                <div className="text-left">
                  <p className="text-sm font-bold text-foreground">
                    {downloading ? "Downloading" : "Export Report"}
                  </p>
                  <p className="text-[10px] d">
                    Choose with or without evidence files
                  </p>
                </div>
              </button>
            </div>
            {report.actions.preview && (
              <div className="relative">
                <button
                  onClick={handlePreview}
                  disabled={previewLoading}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50"
                >
                  {previewLoading ? (
                    <Loader2 className="size-5 animate-spin d" />
                  ) : (
                    <BookOpen className="size-5 text-indigo-500" />
                  )}
                  <div className="text-left">
                    <p className="text-sm font-bold text-foreground">
                      {previewLoading ? "Loading" : "Preview Report"}
                    </p>
                    <p className="text-[10px] d">View HTML report in browser</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={headerDownloadOpen || cardDownloadOpen}
        onClose={() => {
          setHeaderDownloadOpen(false);
          setCardDownloadOpen(false);
        }}
        title="Export Report"
        description="Choose whether to export the report with or without evidence."
        size="sm"
      >
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleDownload(false)}
            disabled={!!downloading}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
          >
            {downloading === "report" ? (
              <Loader2 className="size-6 animate-spin d" />
            ) : (
              <Download className="size-6 d group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
            )}
            <span className="text-sm font-semibold text-center group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              Without Evidence
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleDownload(true)}
            disabled={!!downloading}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-amber-400 dark:hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors group disabled:opacity-40"
          >
            {downloading === "evidence" ? (
              <Loader2 className="size-6 animate-spin d" />
            ) : (
              <FileArchive className="size-6 d group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors" />
            )}
            <span className="text-sm font-semibold text-center group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
              With Evidence
            </span>
          </button>
        </div>
      </Modal>

      {downloading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Download className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting
            </p>
            <p className="text-xs d">
              Please wait while we prepare your report
            </p>
          </div>
        </div>
      )}

      {/* ── Report Preview Modal ── */}
      {(previewHtml || previewLoading) && (
        <ModalShell
          open
          onClose={closePreview}
          zIndexClass="z-[60]"
          variant="preview"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <FileBarChart className="size-4 text-indigo-500" />
              <h3 className="text-sm font-medium text-foreground">
                Report Preview
              </h3>
            </div>
            <button
              onClick={closePreview}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <X className="size-5 d" />
            </button>
          </div>
          {previewLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <Loader2 className="size-8 animate-spin text-indigo-500" />
              <p className="text-sm d">Loading preview</p>
            </div>
          ) : previewHtml ? (
            <iframe
              srcDoc={previewHtml}
              style={{ flex: 1, width: "100%", height: "100%", border: "none" }}
              title="Report Preview"
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </ModalShell>
      )}
    </PageShell>
  );
}
